import { type messages as importMessages } from '../messages.ts';
import {
  type PasteFlagConditions,
  type PasteFlagParams,
  type PasteMessageType
} from '../types.ts';

import { unreachable } from './paste-helper.ts';

/**
 * Gets the id of the title and description messages for the given paste message type. If no id is returned for the
 * description, it means one does not exist and only the title should be displayed.
 */
export const getMessageByType = (
  type: PasteMessageType
): {
  title: keyof typeof importMessages;
  description?: keyof typeof importMessages;
  excludeUndo?: boolean;
} => {
  switch (type) {
    case 'entriesFiltered':
      return {
        title: 'flag-info-message.entries-filtered',
        description: 'flag-info-description.entries-filtered'
      };
    case 'pastedIntoSort':
      return {
        title: 'flag-info-message.pasted-into-sort',
        description: 'flag-info-description.pasted-into-sort'
      };
    case 'structureLocked':
      return {
        title: 'flag-warning-message.structure-locked',
        description: 'flag-warning-description.structure-locked'
      };
    case 'readOnly':
      return {
        title: 'flag-warning-message.read-only',
        description: 'flag-warning-description.read-only',
        excludeUndo: true
      };
    case 'restrictedContentCantBeCopied':
      return {
        title: 'flag-warning-message.restricted-content-cant-be-copied',
        description: 'flag-warning-description.restricted-content-cant-be-copied'
      };
    case 'mismatchedFieldType':
      return {
        title: 'flag-warning-message.pasting-content-that-doesnt-match',
        description: 'flag-warning-description.pasting-content-that-doesnt-match'
      };
    case 'updateFieldSettings':
      return {
        title: 'flag-warning-message.field-settings-updated',
        description: 'flag-warning-description.field-settings-updated'
      };
    case 'cannotReadPastedContent':
      return {
        title: 'flag-error-message.cannot-read-pasted-content',
        excludeUndo: true
      };
    case 'unexpectedDataType':
      return {
        title: 'flag-error-message.unexpected-data-type',
        excludeUndo: true
      };

    default:
      return unreachable(type);
  }
};

export function renderPasteFlags(
  pasteFlagConditions: PasteFlagConditions,
  numberOfEntriesFiltered: number,
  numberOfEntriesPasted: number,
  showFlags: (data: PasteFlagParams[]) => void
) {
  const actionTypes = getPasteFlagType(pasteFlagConditions, numberOfEntriesFiltered);

  displayFlags({
    actionTypes,
    numberOfEntriesFiltered,
    numberOfEntriesPasted,
    showFlags
  });
}

function getPasteFlagType(
  pasteFlagConditions: PasteFlagConditions,
  numberOfEntriesFiltered: number
): PasteFlagParams['actionType'][] {
  const result: PasteFlagParams['actionType'][] = [];

  const {
    isPartialPaste,
    isIncompatiblePasteContent,
    shouldFieldSettingUpdate,
    isPastingIntoSortedField
  } = pasteFlagConditions;

  // The order of the result array will determine the order of the flags displayed.
  // Flags pushed first will be displayed on top.
  if (isPartialPaste) {
    result.push('structureLocked');
  }
  if (numberOfEntriesFiltered > 0) {
    result.push('entriesFiltered');
  }
  if (!isPartialPaste && isIncompatiblePasteContent) {
    result.push('mismatchedFieldType');
  }
  if (shouldFieldSettingUpdate) {
    result.push('updateFieldSettings');
  }
  if (isPastingIntoSortedField) {
    result.push('pastedIntoSort');
  }

  return result;
}

/**
 * Displays the flag for each action type. The flags will be displayed top to bottom in the order of `actionTypes`.
 *
 * For example, an array of ['mismatchedFieldType', 'structureLocked'] will display the
 * flags in the following order:
 * - mismatchedFieldType
 * - structureLocked
 */
function displayFlags({
  actionTypes,
  numberOfEntriesFiltered,
  numberOfEntriesPasted,
  showFlags
}: {
  actionTypes: PasteFlagParams['actionType'][];
  numberOfEntriesFiltered: number;
  numberOfEntriesPasted: number;
  showFlags: (data: PasteFlagParams[]) => void;
}) {
  const flagsToShow: PasteFlagParams[] = [];

  // flags that are pushed to the end of the array will be displayed on top. to respect the priority of actionTypes,
  // we use unshift.
  for (const actionType of actionTypes) {
    switch (actionType) {
      case 'mismatchedFieldType':
      case 'structureLocked':
        flagsToShow.unshift({
          actionType,
          type: 'warning'
        });
        break;
      case 'updateFieldSettings':
        flagsToShow.unshift({
          actionType: 'updateFieldSettings',
          type: 'warning',
          options: {
            showIcon: false
          }
        });
        break;
      case 'pastedIntoSort':
        flagsToShow.unshift({
          actionType: 'pastedIntoSort',
          type: 'info'
        });
        break;
      case 'entriesFiltered':
        flagsToShow.unshift({
          actionType: 'entriesFiltered',
          type: 'info',
          options: {
            messageValues: {
              numberOfEntriesFiltered,
              numberOfEntriesPasted
            }
          }
        });
        break;
    }
  }

  if (flagsToShow.length) {
    showFlags(flagsToShow);
  }
}
